import React, { useEffect, useMemo, useRef, useState } from 'react';

// Themes and cluster color assignment (pre-assigned per bar)
const CLUSTER_COLORS = ['#20B8F2', '#7EE06D', '#A78BFA', '#F2B84C', '#F2704C'];

function buildBars(count) {
  // Distribute ~4-5 clusters of colored bars across the track
  const bars = [];
  const clusters = [];
  // 5 clusters, each 5-8 bars wide
  let cursor = 4;
  for (let c = 0; c < 5; c++) {
    const width = 5 + Math.floor(Math.random() * 4);
    const start = cursor + Math.floor(Math.random() * 6);
    clusters.push({ start, end: start + width, color: CLUSTER_COLORS[c] });
    cursor = start + width + 4;
    if (cursor >= count - 3) break;
  }

  for (let i = 0; i < count; i++) {
    const cluster = clusters.find(c => i >= c.start && i < c.end);
    if (cluster) {
      // Tall + colored
      bars.push({ height: 60 + Math.random() * 35, color: cluster.color, kind: 'highlight' });
    } else {
      // Short + grey
      bars.push({ height: 18 + Math.random() * 27, color: 'rgba(255,255,255,0.20)', kind: 'grey' });
    }
  }
  return bars;
}

export default function HeroDemo() {
  const [phase, setPhase] = useState('scan'); // 'scan' -> 'hold'
  const [scanPct, setScanPct] = useState(0);
  const [counts, setCounts] = useState({ themes: 0, highlights: 0, top: 0 });
  const [flash, setFlash] = useState(false);

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const barCount = isMobile ? 40 : 62;
  const bars = useMemo(() => buildBars(barCount), [barCount]);

  const scanRef = useRef();
  useEffect(() => {
    let raf;
    let start;
    const holdMs = 22000;
    const scanMs = 6000;
    const totalMs = scanMs + holdMs + 800;

    function loop(ts) {
      if (!start) start = ts;
      const t = (ts - start) % totalMs;
      if (t < scanMs) {
        const p = t / scanMs;
        setScanPct(p * 100);
        setCounts({
          themes: Math.round(easeOut(clamp(p / 0.4)) * 4),
          highlights: Math.round(easeOut(clamp(p / 0.8)) * 12),
          top: Number((easeOut(clamp(p / 1)) * 9.4).toFixed(1)),
        });
        setPhase('scan');
      } else if (t < scanMs + 500) {
        setScanPct(100);
        setCounts({ themes: 4, highlights: 12, top: 9.4 });
        setFlash(true);
        setPhase('hold');
      } else {
        setFlash(false);
        setScanPct(100);
        setCounts({ themes: 4, highlights: 12, top: 9.4 });
        setPhase('hold');
      }
      raf = requestAnimationFrame(loop);
    }
    // Respect reduced motion
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setScanPct(100);
      setCounts({ themes: 4, highlights: 12, top: 9.4 });
      setPhase('hold');
      return;
    }
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      data-testid="hero-demo-illustration"
      className={`aurora-panel p-5 sm:p-6 relative transition-all duration-500 ${flash ? 'ring-1 ring-[#7EE06D]/40' : ''}`}
      style={{ boxShadow: '0 30px 80px -30px rgba(32,184,242,0.3)' }}
    >
      {/* header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-[#7EE06D] shadow-[0_0_8px_#7EE06D]" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">Live Demo · Auto-Loop</span>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">SIMNOPSIS</span>
      </div>

      {/* Waveform panel */}
      <div className="relative inner-dark p-4 sm:p-5 h-48 sm:h-56 overflow-hidden !border !border-white/35">
        <div className="absolute inset-0 flex items-end px-4 pb-4 gap-[2px]">
          {bars.map((b, i) => {
            const revealed = (i / bars.length) * 100 <= scanPct;
            return (
              <div
                key={i}
                className="demo-bar"
                style={{
                  height: revealed ? `${b.height}%` : '4%',
                  background: revealed ? b.color : 'rgba(255,255,255,0.08)',
                  opacity: revealed ? (b.kind === 'highlight' ? 1 : 0.9) : 0.5,
                  boxShadow: revealed && b.kind === 'highlight' ? `0 0 12px ${b.color}55` : 'none',
                }}
              />
            );
          })}
        </div>
        {/* Scan line */}
        {phase === 'scan' && (
          <div
            className="absolute top-4 bottom-4 w-[2px]"
            style={{
              left: `calc(${scanPct}% - 1px)`,
              background: 'linear-gradient(180deg, transparent 0%, #20B8F2 40%, #20B8F2 60%, transparent 100%)',
              boxShadow: '0 0 20px #20B8F2, 0 0 40px rgba(32,184,242,0.4)',
              opacity: scanPct > 0 && scanPct < 100 ? 1 : 0,
              transition: 'opacity 200ms',
            }}
          />
        )}
      </div>

      {/* KPI counters */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4 mt-4">
        <KPI label="Themes" value={counts.themes} />
        <KPI label="Highlights" value={counts.highlights} />
        <KPI label="Top Pick" value={counts.top.toFixed(1)} />
      </div>
    </div>
  );
}

function KPI({ label, value }) {
  return (
    <div className="inner-dark relative flex min-h-[88px] flex-col items-center justify-center overflow-hidden px-3 py-4 text-center sm:min-h-[96px] sm:px-4 !border !border-white/35">
      <div className="pointer-events-none absolute inset-x-4 top-0 h-px bg-gradient-to-r from-transparent via-white/12 to-transparent" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(255,255,255,0.04),transparent_55%)]" />
      <div className="relative font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">{label}</div>
      <div className="relative mt-2 font-display text-2xl font-semibold leading-none text-white tabular-nums sm:text-[1.75rem]">
        {value}
      </div>
    </div>
  );
}

function easeOut(x) { return 1 - Math.pow(1 - x, 3); }
function clamp(x) { return Math.max(0, Math.min(1, x)); }
